import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useInstallationContext } from '../../context/installation';
import {
  InstallationPermissions,
  Me,
  UserMeQuery,
  UserMeQueryVariables,
  UserProfileQuery,
  UserProfileQueryVariables,
} from '../../data-access/gql-types/graphql';
import { USER_ME, USER_PROFILE } from '../../data-access/queries/user';
import { useNotificationsStore } from '../../store/notifications';
import { useUserProfileStore } from '../../store/user-profile';
import { mapPermissions } from '../../utils/helpers/permissions';
import { useSentry } from '../sentry/use-sentry';

export const useFetchUser = () => {
  const { setSentryUser } = useSentry();
  const { selectedInstallationId } = useInstallationContext();
  const setCountOfUnreadNotifications = useNotificationsStore((s) => s.setCountOfUnreadNotifications);
  const {
    setMeData,
    setProfileData,
    setPermissions,
    setLoadingUser,
    shouldRefetchUserMe,
    shouldRefetchUserProfile,
    resetRefetchUserMe,
    resetRefetchUserProfile,
  } = useUserProfileStore((s) => s);

  const {
    data: userMe,
    refetch: refetchMe,
    loading: loadingUserMe,
  } = useQuery<UserMeQuery, UserMeQueryVariables>(USER_ME, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });

  const {
    data: userProfile,
    refetch: refetchProfile,
    loading: loadingUserProfile,
  } = useQuery<UserProfileQuery, UserProfileQueryVariables>(USER_PROFILE, {
    variables: { installationId: selectedInstallationId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    skip: !selectedInstallationId,
  });

  useEffect(() => {
    setLoadingUser(loadingUserMe || loadingUserProfile);
  }, [loadingUserMe, loadingUserProfile]);

  const setUserMeData = (me: Me) => {
    const profile = me.profile;

    if (profile) {
      setMeData({
        userId: profile.id,
        email: profile.email,
        firstName: profile.firstName,
        userInitial: profile.firstName.charAt(0),
        imageUrl: me.profileImage?.imageUrl || '',
      });

      setSentryUser(profile.id, profile.email);
    }

    const permissions = mapPermissions((me.permissions || []) as InstallationPermissions[]);
    setPermissions(permissions);
  };

  useEffect(() => {
    if (userMe?.me) setUserMeData(userMe.me as Me);
  }, [userMe?.me]);

  useEffect(() => {
    if (userProfile?.userProfile) {
      setProfileData(userProfile.userProfile);
      setCountOfUnreadNotifications(userProfile.userProfile.countOfUnreadNotifications || 0);
    }
  }, [userProfile?.userProfile]);

  const fetchUserMe = async () => {
    resetRefetchUserMe();

    try {
      const { data } = await refetchMe();

      if (data?.me) setUserMeData(data.me as Me);
    } catch (err) {
      console.error('Refetch failed:', err);
    }
  };

  useEffect(() => {
    if (shouldRefetchUserMe) fetchUserMe();
  }, [shouldRefetchUserMe, fetchUserMe]);

  const fetchProfile = async () => {
    resetRefetchUserProfile();

    try {
      const { data } = await refetchProfile({ installationId: selectedInstallationId });

      if (data?.userProfile) {
        setProfileData(data.userProfile);
        setCountOfUnreadNotifications(data.userProfile.countOfUnreadNotifications || 0);
      }
    } catch (err) {
      console.error('Refetch failed:', err);
    }
  };

  useEffect(() => {
    if (shouldRefetchUserProfile && selectedInstallationId) fetchProfile();
  }, [shouldRefetchUserProfile, selectedInstallationId, fetchProfile]);
};
